import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { throwIfNotOk } from "@/lib/api-error";
import { CaretDown, Check, MagnifyingGlass, User, X } from "@phosphor-icons/react";
import type { TherapistListItem } from "@/types/therapist";

interface TherapistComboboxProps {
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
  className?: string;
  /**
   * Natpis kad `value` nije među ponuđenim (aktivnim) terapeutima, npr. veza na
   * terapeuta koji je u međuvremenu deaktiviran. Bez ovoga trigger pokazuje
   * placeholder iako je vrijednost i dalje postavljena.
   */
  unlistedLabel?: string;
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function useApprovedTherapists() {
  return useQuery({
    queryKey: ["therapists", "approved"],
    queryFn: async () => {
      const res = await fetch("/api/therapists?status=approved");
      await throwIfNotOk(res, "Greška pri učitavanju terapeuta");
      return res.json() as Promise<TherapistListItem[]>;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function TherapistCombobox({
  value,
  onChange,
  placeholder = "Svi terapeuti",
  className,
  unlistedLabel,
}: TherapistComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const { data: therapists, isLoading } = useApprovedTherapists();

  const options = useMemo(() => {
    const list = therapists ?? [];
    return [...list].sort((a, b) => a.userName.localeCompare(b.userName));
  }, [therapists]);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => o.userName.toLowerCase().includes(q));
  }, [options, query]);

  const selected = value ? options.find((o) => o.id === value) ?? null : null;
  const unlisted = value && !selected && !isLoading && unlistedLabel ? unlistedLabel : null;

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-sm transition-colors",
            "hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            className
          )}
        >
          {selected ? (
            <>
              <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[9px] font-bold text-primary-foreground">
                {getInitials(selected.userName)}
              </span>
              <span className="flex-1 truncate text-left text-foreground">
                {selected.userName}
              </span>
            </>
          ) : unlisted ? (
            <>
              <User className="size-4 text-muted-foreground" />
              <span className="flex-1 truncate text-left text-foreground">{unlisted}</span>
            </>
          ) : (
            <>
              <User className="size-4 text-muted-foreground" />
              <span className="flex-1 truncate text-left text-muted-foreground">
                {placeholder}
              </span>
            </>
          )}
          <CaretDown className="size-3.5 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        <div className="border-b border-border/40 p-2">
          <div className="relative">
            <MagnifyingGlass className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Pretraži terapeuta..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-8 pl-8 text-xs"
              autoFocus
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setOpen(false);
              setQuery("");
            }}
            className={cn(
              "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors",
              value === null
                ? "bg-primary/[0.06] text-foreground"
                : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"
            )}
          >
            <X className="size-3.5" />
            <span className="flex-1">{placeholder}</span>
            {value === null && <Check className="size-3.5 shrink-0 text-primary" />}
          </button>
          {unlisted && (
            <div className="flex w-full items-center gap-2 rounded-md bg-primary/[0.06] px-2 py-1.5 text-left text-xs text-foreground">
              <User className="size-3.5" />
              <span className="flex-1 truncate">{unlisted}</span>
              <Check className="size-3.5 shrink-0 text-primary" />
            </div>
          )}
          <div className="my-1 border-t border-border/30" />
          {isLoading ? (
            <p className="px-2 py-6 text-center text-xs text-muted-foreground">—</p>
          ) : filtered.length === 0 ? (
            <p className="px-2 py-6 text-center text-xs text-muted-foreground">—</p>
          ) : (
            filtered.map((o) => {
              const active = o.id === value;
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => {
                    onChange(o.id);
                    setOpen(false);
                    setQuery("");
                  }}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors",
                    active
                      ? "bg-primary/[0.06] text-foreground"
                      : "text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                  )}
                >
                  <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[9px] font-bold text-primary-foreground">
                    {getInitials(o.userName)}
                  </span>
                  <span className="flex-1 truncate font-medium">{o.userName}</span>
                  {active && <Check className="size-3.5 shrink-0 text-primary" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
